import { Button } from "@components/controls/Button"
import { AGDALogo } from "@components/svg/AGDALogo"
import { AuthButtons } from "./AuthButtons"
import { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Menu } from "@components/atoms/Menu"
import { useNewsletterSubscribe } from "@hooks/queries"
import clsx from "clsx"

export function Footer() {
  const footerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [bottomHeight, setBottomHeight] = useState(0)
  const [footerHeight, setFooterHeight] = useState(0)
  const [fixed, setFixed] = useState<boolean>(false)

  useEffect(() => {
    const handleResize = () => {
      if (bottomRef.current) {
        setBottomHeight(bottomRef.current.offsetHeight)
      }
      if (footerRef.current) {
        setFooterHeight(footerRef.current.offsetHeight)
      }
    }

    const handleScroll = () => {
      if (bottomRef.current) {
        setFixed(window.scrollY >= footerHeight)
      }
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", handleResize)
    handleResize()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const [typePopup, setTypePopup] = useState<boolean>(false)

  return (
    <>
      <motion.footer
        ref={footerRef}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="subtheme-dark bg-bg text-fg py-5 flex flex-col gap-8 md:gap-9 md:min-h-[calc(100vh-var(--spacing-headerHeight))]"
      >
        {/* Logo & Login/Logout shortcuts */}
        <div className="grid-auto">
          <div className="col-span-12 md:col-span-6 pb-7 md:pb-0">
            <AGDALogo className="w-full h-auto fill-fg" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <AuthButtons buttonVariant="reversed" />
          </div>
        </div>

        {/* Subscribe */}
        <div className="grid-container">
          <NewsletterForm />
        </div>

        <div
          ref={bottomRef}
          className={clsx("w-full flex flex-col", fixed && "sticky")}
          style={{
            top: fixed ? `calc(100vh - ${bottomHeight}px - var(--spacing-5))` : undefined,
          }}
        >
          {/* Menu */}
          <div className="grid-auto type-body-m-roman">
            <div className="col-span-6 md:col-span-2 mt-4">
              <Menu location="Discover" maxDepth={0} className="[&>li]:mb-3" />
            </div>
            <div className="col-span-6 md:col-span-2 mt-4">
              <Menu location="Corporate" maxDepth={0} className="[&>li]:mb-3" />
            </div>
            <div className="col-span-6 md:col-span-2 mt-4">
              <Menu location="Social" maxDepth={0} className="[&>li]:mb-3" />
            </div>
            <div className="col-span-6 md:col-span-2 mt-4">
              <Menu location="Legal" maxDepth={0} className="[&>li]:mb-3" />
            </div>
          </div>

          {/* More stuff */}
          <div className="grid-auto">
            <div className="col-span-12 md:col-span-7 text-pretty type-body-m-roman text-grey flex flex-col el-p:mt-[0.6em]">
              <p>
                AGDA acknowledges the First Peoples of Australia, their Elders past, present and emerging. We pay our respects to the traditional storytellers, designers, artists
                and owners of the land on which we live and work.
              </p>
              <p>&copy; {new Date().getFullYear()} Australian Graphic Design Association.</p>
            </div>
            <div className="col-span-12 md:col-span-5 text-right mt-5 md:mt-0 type-body-m-roman flex md:items-end md:justify-end gap-4 relative">
              <a
                className="text-grey hover:text-grey-light transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  setTypePopup(!typePopup)
                }}
              >
                Typography
              </a>
              <a href="https://ed.studio/" target="_blank" className="text-grey hover:text-grey-light transition-colors">
                Site by ED.
              </a>
              <AnimatePresence>
                {typePopup && (
                  <motion.div
                    exit={{ opacity: 0, y: "-95%" }}
                    initial={{ opacity: 0, y: "-95%" }}
                    animate={{ opacity: 1, y: "-100%" }}
                    transition={{
                      stiffness: 400,
                    }}
                    className="absolute z-50 -top-6 md:top-0 left-0 md:left-auto md:right-0"
                  >
                    <TypographyPopup setTypePopup={setTypePopup} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.footer>
    </>
  )
}

const TypographyPopup = ({ setTypePopup }: { setTypePopup: (value: React.SetStateAction<boolean>) => void }) => {
  return (
    <div className="bg-grey-light w-[280px] flex flex-col pt-5 md:pt-3 pb-7 md:pb-5 px-5 md:px-3 gap-5 text-black text-left">
      <div className="flex flex-col gap-3">
        <label className="type-ui-s-roman-caps">Typography</label>
        <p className="type-title-m-medium text-grey-mid">
          <strong className="text-black type-title-m-medium ">Neue Haas Grotesk Display Pro</strong>
          <br />A nod to design history.
        </p>
        <span className="type-body-s-serif text-grey-mid">
          by Christian Schwartz, Max Miedinger (
          <a href="https://www.myfonts.com/collections/neue-haas-grotesk-font-linotype" className="underline hover:no-underline" target="_blank">
            Linotype
          </a>
          )
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <p className="type-title-m-serif text-grey-mid">
          <strong className="text-black type-title-m-serif">Herbik</strong>
          <br />
          Championing local design talent.
        </p>
        <span className="type-body-s-serif text-grey-mid">
          by Daniel Veneklaas (
          <a href="https://counter-forms.com/typefaces/herbik" className="underline hover:no-underline" target="_blank">
            Counter Forms
          </a>
          )
        </span>
      </div>
      <Button className="self-start" onClick={() => setTypePopup(false)}>
        Close
      </Button>
    </div>
  )
}

function NewsletterForm() {
  const [email, setEmail] = useState("")
  const subscribe = useNewsletterSubscribe()

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        subscribe.mutate({ email })
      }}
      className="stack-y-2"
    >
      <div className="type-ui-s-roman-caps text-grey">Sign up for industry news, events, and insights</div>
      <input
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        className="bg-transparent border-fg border-b type-title-l-medium pb-3 antialiased outline-none placeholder:text-white/40"
        placeholder="you@internet.web"
      />
      <div className="mt-4">
        <Button type="submit" variant="reversed" loading={subscribe.isPending}>
          Subscribe
        </Button>
      </div>
    </form>
  )
}
